import React, { useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Popover,
  Progress,
  ProgressMeasureLocation,
  ProgressStep,
  ProgressStepper,
  ProgressVariant,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, ExternalLinkAltIcon, InProgressIcon, OutlinedClockIcon, PencilAltIcon, TrashIcon } from '@patternfly/react-icons';
import { useTimeTracker } from '../../context/TimeTrackerContext';
import { useTimer } from '../../context/TimerContext';
import { useWorklogEdit } from '../../hooks';
import { EditTimeLogModal } from '../EditTimeLogModal';
import { ConfirmModal } from '../ConfirmModal';
import { TimeLog } from '../../types';
import { detectOverlaps, formatTimeOnly, formatTimeSpent } from '../../utils';
import { DAILY_GOAL_SECONDS, JIRA_BASE_URL } from '../../config';
import './TodayTimeLog.css';

const GAP_THRESHOLD_MINUTES = 15; // Minutes gap to create a new session block

export const TodayTimeLog: React.FC = () => {
  const { todayTimeLogs, todayTimeLogsLoading, updateTimeLog, deleteTimeLog } =
    useTimeTracker();
  const { activeTimer, elapsedTime } = useTimer();

  const { editModalProps, deleteModalProps, handleEdit, handleDeleteClick } = useWorklogEdit({
    onSave: useCallback(
      async (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => {
        await updateTimeLog(id, startedTime, timeSpentSeconds, labels);
      },
      [updateTimeLog]
    ),
    onDelete: useCallback(
      async (id: string) => {
        await deleteTimeLog(id);
      },
      [deleteTimeLog]
    ),
  });

  // Calculate daily progress
  const dailyProgress = useMemo(() => {
    const loggedSeconds = todayTimeLogs.reduce((sum, log) => sum + log.timeSpentSeconds, 0);
    const activeSeconds = activeTimer ? elapsedTime : 0;
    const totalSeconds = loggedSeconds + activeSeconds;
    const remainingSeconds = Math.max(0, DAILY_GOAL_SECONDS - totalSeconds);
    const percentage = Math.min(100, (totalSeconds / DAILY_GOAL_SECONDS) * 100);
    const isComplete = totalSeconds >= DAILY_GOAL_SECONDS;

    return {
      totalSeconds,
      remainingSeconds,
      percentage,
      isComplete,
    };
  }, [todayTimeLogs, activeTimer, elapsedTime]);

  // Build allLogs array with active timer included
  const allLogs = useMemo(() => {
    const logs = [...todayTimeLogs];
    if (activeTimer) {
      logs.unshift({
        id: 'active',
        name: 'active',
        jiraId: 'active',
        issueId: activeTimer.issueId,
        authorId: '',
        startedTime: activeTimer.startTime.toISOString(),
        timeSpentSeconds: elapsedTime,
        createTime: activeTimer.startTime.toISOString(),
        syncTime: activeTimer.startTime.toISOString(),
        issueKey: activeTimer.issueKey,
        issueSummary: activeTimer.issueSummary,
      });
    }
    return logs;
  }, [todayTimeLogs, activeTimer, elapsedTime]);

  // Detect overlapping time logs
  const overlappingLogs = useMemo(() => {
    return detectOverlaps(allLogs, (log) => log.issueKey || log.issueId || log.id);
  }, [allLogs]);

  // Group logs into sessions based on time gaps
  // A new session starts when there's a gap > GAP_THRESHOLD_MINUTES between consecutive logs
  const sessionGroups = useMemo(() => {
    if (allLogs.length === 0) return [];

    const groups: TimeLog[][] = [];
    let currentGroup: TimeLog[] = [];

    // allLogs is sorted by startedTime descending (newest first)
    for (let i = 0; i < allLogs.length; i++) {
      const currentLog = allLogs[i];

      if (currentGroup.length === 0) {
        currentGroup.push(currentLog);
      } else {
        // Get the previous log in the group (which is newer in time)
        const prevLog = currentGroup[currentGroup.length - 1];

        // Calculate end time of current log and start time of previous log
        const currentEndTime = new Date(currentLog.startedTime).getTime() + currentLog.timeSpentSeconds * 1000;
        const prevStartTime = new Date(prevLog.startedTime).getTime();

        // Gap between current log's end and previous log's start
        const gapMinutes = (prevStartTime - currentEndTime) / (1000 * 60);

        if (gapMinutes > GAP_THRESHOLD_MINUTES) {
          // Start a new group
          groups.push(currentGroup);
          currentGroup = [currentLog];
        } else {
          currentGroup.push(currentLog);
        }
      }
    }

    // Don't forget the last group
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [allLogs]);

  const getEndTime = (timeLog: TimeLog): string => {
    const startDate = new Date(timeLog.startedTime);
    const endDate = new Date(startDate.getTime() + timeLog.timeSpentSeconds * 1000);
    return formatTimeOnly(endDate.toISOString());
  };

  if (todayTimeLogsLoading) {
    return (
      <Card isFullHeight>
        <CardHeader>
          <CardTitle>
            <ClockIcon /> Today
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="today-timelog-loading">
            <Spinner size="lg" />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card isFullHeight>
        <CardHeader>
          <CardTitle>
            <ClockIcon /> Today
          </CardTitle>
        </CardHeader>
        <CardBody>
          {/* Daily Progress Section */}
          <div className="daily-progress-section">
            <Progress
              value={dailyProgress.percentage}
              title="Your progress"
              label={`${formatTimeSpent(dailyProgress.totalSeconds)} / 8h`}
              measureLocation={ProgressMeasureLocation.top}
              variant={dailyProgress.isComplete ? ProgressVariant.success : undefined}
              aria-label="Daily work progress"
            />
            <div className="daily-stats">
              <div className="daily-stat">
                <CheckCircleIcon />
                <span className="daily-stat-label">Logged:</span>
                <span className="daily-stat-value">{formatTimeSpent(dailyProgress.totalSeconds)}</span>
              </div>
              <div className="daily-stat">
                <OutlinedClockIcon />
                <span className="daily-stat-label">
                  {dailyProgress.isComplete ? 'Goal reached' : 'To go:'}
                </span>
                <span className="daily-stat-value">
                  {dailyProgress.isComplete ? '' : formatTimeSpent(dailyProgress.remainingSeconds)}
                </span>
              </div>
            </div>
          </div>

          {/* Time Logs List */}
          {allLogs.length === 0 ? (
            <EmptyState titleText="Ready to start?" icon={ClockIcon} headingLevel="h4">
              <EmptyStateBody>Select an issue from the list to begin tracking your time.</EmptyStateBody>
            </EmptyState>
          ) : (
            <div className="timelog-sessions">
              {sessionGroups.map((group, groupIndex) => (
                <div key={`session-${groupIndex}`} className="timelog-session-block">
                  <ProgressStepper isVertical aria-label={`Work session ${groupIndex + 1}`}>
                    {group.map((log) => {
                      const isActive = log.id === 'active';
                      const issueKey = log.issueKey || log.issueId;
                      const issueSummary = log.issueSummary || '';
                      const timeRange = `${formatTimeOnly(log.startedTime)} - ${isActive ? '...' : getEndTime(log)}`;
                      const overlappingWith = overlappingLogs.get(log.id);
                      const hasOverlap = overlappingWith && overlappingWith.length > 0;

                      const description = isActive
                        ? `${timeRange} • Tracking...`
                        : `${timeRange} • ${formatTimeSpent(log.timeSpentSeconds)}`;

                      // Determine variant: warning for overlap, info for active, success for normal
                      const stepVariant = hasOverlap ? 'warning' : (isActive ? 'info' : 'success');
                      const stepIcon = hasOverlap
                        ? <ExclamationTriangleIcon />
                        : (isActive ? <InProgressIcon className="timelog-icon-spin" /> : undefined);

                      return (
                        <ProgressStep
                          key={log.id}
                          variant={stepVariant}
                          isCurrent={isActive}
                          icon={stepIcon}
                          titleId={`step-${log.id}`}
                          aria-label={`${issueKey}: ${timeRange}`}
                        >
                          <Flex
                            justifyContent={{ default: 'justifyContentSpaceBetween' }}
                            alignItems={{ default: 'alignItemsFlexStart' }}
                            className="timelog-step-content"
                          >
                            <FlexItem className="timelog-step-info">
                              <div className="timelog-step-key">
                                <Popover
                                  headerContent={issueKey}
                                  bodyContent={issueSummary || 'No description'}
                                  triggerAction="hover"
                                  position="top"
                                >
                                  <strong className="timelog-step-key-text">{issueKey}</strong>
                                </Popover>
                                {issueSummary && <span className="timelog-step-summary"> - {issueSummary}</span>}
                              </div>
                              <div className="timelog-step-description">
                                {description}
                                {hasOverlap && (
                                  <Tooltip content={`Overlaps with: ${overlappingWith.join(', ')}`}>
                                    <span className="timelog-overlap-warning"> (overlap)</span>
                                  </Tooltip>
                                )}
                              </div>
                            </FlexItem>
                            {!isActive && (
                              <FlexItem className="timelog-step-actions">
                                <Tooltip content="Edit">
                                  <Button
                                    variant="plain"
                                    icon={<PencilAltIcon />}
                                    onClick={() => handleEdit(log)}
                                    aria-label="Edit time log"
                                    size="sm"
                                  />
                                </Tooltip>
                                <Tooltip content="Open in Jira">
                                  <Button
                                    variant="plain"
                                    icon={<ExternalLinkAltIcon />}
                                    component="a"
                                    href={`${JIRA_BASE_URL}/browse/${issueKey}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open in Jira"
                                    size="sm"
                                  />
                                </Tooltip>
                                <Tooltip content="Delete">
                                  <Button
                                    variant="plain"
                                    icon={<TrashIcon />}
                                    onClick={() => handleDeleteClick(log.id)}
                                    aria-label="Delete time log"
                                    size="sm"
                                  />
                                </Tooltip>
                              </FlexItem>
                            )}
                          </Flex>
                        </ProgressStep>
                      );
                    })}
                  </ProgressStepper>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <EditTimeLogModal {...editModalProps} />
      <ConfirmModal {...deleteModalProps} />
    </>
  );
};
